import React, { useState, useEffect } from "react";
import { useTranslation } from "../../../i18n/LanguageContext";
import StripePaymentAPI from "../services/StripePaymentAPI";
import RefreshButton from "../common/RefreshButton";
import { formatDateOnly } from "../utils/DateTimeUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheck,
  faSpinner,
  faClock,
  faXmark,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";
import { faCreditCard } from "@fortawesome/free-solid-svg-icons";
import {
  faCcVisa,
  faCcMastercard,
  faCcAmex,
  faCcDiscover,
} from "@fortawesome/free-brands-svg-icons";
import toast from "react-hot-toast";
import "../css/Common.css";

const PaymentHistory = () => {
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [dateFilter, setDateFilter] = useState("last30");
  const [statusFilter, setStatusFilter] = useState("all");
  const { t } = useTranslation();

  const getCardBrandIcon = (brand) => {
    const brandLower = brand?.toLowerCase();
    switch (brandLower) {
      case "visa":
        return <FontAwesomeIcon icon={faCcVisa} />;
      case "mastercard":
        return <FontAwesomeIcon icon={faCcMastercard} />;
      case "amex":
      case "american express":
        return <FontAwesomeIcon icon={faCcAmex} />;
      case "discover":
        return <FontAwesomeIcon icon={faCcDiscover} />;
      default:
        return <FontAwesomeIcon icon={faCreditCard} />;
    }
  };

  useEffect(() => {
    loadPaymentHistory();
  }, []);

  // Re-fetch billing history after a plan change so upgrade charges appear
  useEffect(() => {
    const handleSubscriptionUpdated = () => {
      // Delay to allow Stripe webhook to process and create the Payment record
      setTimeout(() => loadPaymentHistory(), 3000);
    };
    window.addEventListener("subscriptionUpdated", handleSubscriptionUpdated);
    return () =>
      window.removeEventListener(
        "subscriptionUpdated",
        handleSubscriptionUpdated,
      );
  }, []);

  const loadPaymentHistory = async () => {
    try {
      setLoading(true);
      const payments = await StripePaymentAPI.getPaymentHistory();
      setPaymentHistory(payments);
    } catch (error) {
      console.error("Error loading payment history:", error);
      toast.error(t("paymentHistory.toast.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleReconcile = async () => {
    try {
      setReconciling(true);
      const result = await StripePaymentAPI.reconcilePayments();
      if (result?.paymentsRecovered > 0) {
        toast.success(
          result.message ||
            t("paymentHistory.toast.recovered").replace(
              "{count}",
              result.paymentsRecovered,
            ),
        );
        await loadPaymentHistory();
      } else {
        toast.success(result?.message || t("paymentHistory.toast.upToDate"));
      }
    } catch (error) {
      console.error("Error reconciling payments:", error);
      toast.error(t("paymentHistory.toast.refreshFailed"));
    } finally {
      setReconciling(false);
    }
  };

  // Format date helper - using centralized DateTimeUtil
  const formatDate = (dateString) => {
    return formatDateOnly(dateString, "short");
  };

  // Format currency
  const formatCurrency = (amount, currency = "USD") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  // Format Stripe-generated descriptions into user-friendly text
  const formatDescription = (description) => {
    if (!description) return t("paymentHistory.description.monthly");

    // "Unused time on <Plan> (with X% off) after <date> (with X% off)"
    //  → "Upgrade with credit from <Plan> (X% off)"
    const unusedMatch = description.match(
      /^Unused time on (.+?)(?:\s*\(with .+?\))?\s+after\s+.*/i,
    );
    if (unusedMatch) {
      const planName = unusedMatch[1].trim();
      const pctMatch = description.match(/\(with\s+([\d.]+%?)\s*off\)/i);
      const discount = pctMatch ? ` (${pctMatch[1]} off)` : "";
      return `Upgrade with credit from ${planName}${discount}`;
    }

    // "Remaining time on <Plan> (with X% off) after <date>"
    //  → "Upgraded to <Plan> (X% off)"
    const remainMatch = description.match(
      /^Remaining time on (.+?)(?:\s*\(with .+?\))?\s+after\s+.*/i,
    );
    if (remainMatch) {
      const planName = remainMatch[1].trim();
      const pctMatch = description.match(/\(with\s+([\d.]+%?)\s*off\)/i);
      const discount = pctMatch ? ` (${pctMatch[1]} off)` : "";
      return `Upgraded to ${planName}${discount}`;
    }

    // "1 × <Plan> (at $X.XX / month)" → "<Plan> — $X.XX/mo"
    const subMatch = description.match(
      /^1\s*×\s*(.+?)\s*\(at\s*\$?([\d.]+)\s*\/\s*month\)/i,
    );
    if (subMatch) {
      return `${subMatch[1].trim()} — $${subMatch[2]}/mo`;
    }

    // Already friendly (e.g. "Plan upgrade: Individual → Family") — pass through
    return description;
  };

  // Get status badge class
  const getStatusClass = (status) => {
    const statusClasses = {
      SUCCEEDED: "status-badge completed",
      PROCESSING: "status-badge pending",
      PENDING: "status-badge pending",
      FAILED: "status-badge failed",
      CANCELED: "status-badge failed",
      REFUNDED: "status-badge refunded",
    };
    return statusClasses[status] || "status-badge";
  };

  // Render compact icon for status (shown on mobile)
  const renderStatusIcon = (status) => {
    switch (status) {
      case "SUCCEEDED":
        return <FontAwesomeIcon icon={faCheck} />;
      case "PROCESSING":
        return <FontAwesomeIcon icon={faSpinner} />;
      case "PENDING":
        return <FontAwesomeIcon icon={faClock} />;
      case "FAILED":
      case "CANCELED":
        return <FontAwesomeIcon icon={faXmark} />;
      case "REFUNDED":
        return <FontAwesomeIcon icon={faRotateLeft} />;
      default:
        return status;
    }
  };

  // Filter payments
  const getFilteredPayments = () => {
    let filtered = paymentHistory;

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (payment) =>
          payment.status.toLowerCase() === statusFilter.toLowerCase(),
      );
    }

    // Filter by date
    const now = new Date();
    if (dateFilter !== "all") {
      filtered = filtered.filter((payment) => {
        const paymentDate = new Date(payment.paymentDate);
        const daysDiff = Math.floor(
          (now - paymentDate) / (1000 * 60 * 60 * 24),
        );

        switch (dateFilter) {
          case "last30":
            return daysDiff <= 30;
          case "last90":
            return daysDiff <= 90;
          case "last180":
            return daysDiff <= 180;
          case "last365":
            return daysDiff <= 365;
          default:
            return true;
        }
      });
    }

    return filtered;
  };

  const filteredPayments = getFilteredPayments();

  if (loading) {
    return <div className="common-loading">{t("paymentHistory.loading")}</div>;
  }

  return (
    <>
      <div className="common-form-group">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <h4>{t("paymentHistory.title")}</h4>
          <RefreshButton
            onClick={handleReconcile}
            loading={reconciling}
            variant="default"
            label={t("paymentHistory.refreshLabel")}
            loadingLabel={t("paymentHistory.refreshing")}
            title={t("paymentHistory.refreshTitle")}
          />
        </div>
        <div className="common-form-row">
          <div className="common-form-field">
            <label>{t("paymentHistory.filter.dateLabel")}</label>
            <select
              className="common-select"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            >
              <option value="all">{t("paymentHistory.filter.allTime")}</option>
              <option value="last30">
                {t("paymentHistory.filter.last30")}
              </option>
              <option value="last90">
                {t("paymentHistory.filter.last90")}
              </option>
              <option value="last180">
                {t("paymentHistory.filter.last180")}
              </option>
              <option value="last365">
                {t("paymentHistory.filter.last365")}
              </option>
            </select>
          </div>
          <div className="common-form-field">
            <label>{t("paymentHistory.filter.statusLabel")}</label>
            <select
              className="common-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">{t("paymentHistory.filter.all")}</option>
              <option value="succeeded">
                {t("paymentHistory.filter.completed")}
              </option>
              <option value="processing">
                {t("paymentHistory.filter.processing")}
              </option>
              <option value="pending">
                {t("paymentHistory.filter.pending")}
              </option>
              <option value="failed">
                {t("paymentHistory.filter.failed")}
              </option>
            </select>
          </div>
        </div>

        {filteredPayments && filteredPayments.length > 0 ? (
          <div className="common-table-container">
            <table className="common-table payment-history-table">
              <thead>
                <tr>
                  <th>{t("paymentHistory.table.date")}</th>
                  <th>{t("paymentHistory.table.description")}</th>
                  <th className="th-amount">
                    <span className="th-full">
                      {t("paymentHistory.table.amount")}
                    </span>
                    <span className="th-short">
                      {t("paymentHistory.table.amountShort")}
                    </span>
                  </th>
                  <th>{t("paymentHistory.table.method")}</th>
                  <th className="th-status">
                    <span className="th-full">
                      {t("paymentHistory.table.status")}
                    </span>
                    <span className="th-short">
                      <FontAwesomeIcon icon={faCheck} />
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredPayments.map((payment) => (
                  <tr key={payment.paymentId}>
                    <td>{formatDate(payment.paymentDate)}</td>
                    <td>{formatDescription(payment.description)}</td>
                    <td>{formatCurrency(payment.amount, payment.currency)}</td>
                    <td>
                      {payment.cardLast4 ? (
                        <span className="payment-method-display">
                          <span className="payment-method-icon">
                            {getCardBrandIcon(payment.cardBrand)}
                          </span>
                          {" •••• "}
                          {payment.cardLast4}
                        </span>
                      ) : (
                        payment.paymentMethod
                      )}
                    </td>
                    <td>
                      <span
                        className={getStatusClass(payment.status)}
                        title={payment.status}
                      >
                        <span className="status-text">{payment.status}</span>
                        <span className="status-icon">
                          {renderStatusIcon(payment.status)}
                        </span>
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="common-no-data indented-below-h4">
            {paymentHistory.length === 0
              ? t("paymentHistory.noHistory")
              : t("paymentHistory.noMatch")}
          </p>
        )}
      </div>
    </>
  );
};

export default PaymentHistory;
